import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { map, Observable } from "rxjs";
import {
  Account,
  AccountCreate
} from "src/app/backend/models/organization/organization";
import { UserNotify } from "src/app/support/notify-decorator";
import { omitFalsyProperties } from "src/app/support/object";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";

@Injectable()
export class AccountBackend {
  private readonly http = inject(HttpClient);

  @UserNotify({
    successMessage: marker("Account creato"),
    errorMessage: marker("Impossibile creare il account")
  })
  create({
    domain,
    account
  }: {
    domain: string;
    account: Partial<AccountCreate>;
  }): Observable<Account> {
    return this.http.post<Account>(
      `${environment.snoots.api.organization}/domains/${domain}/accounts`,
      account
    );
  }

  load(id: string): Observable<Account> {
    return this.http.get<Account>(
      `${environment.snoots.api.organization}/accounts/${id}`
    );
  }

  loadPublic(id: string): Observable<Account> {
    return this.http.get<Account>(
      `${environment.snoots.api.organization}/public/accounts/${id}`
    );
  }

  listBySlugPublic({ slug }: { slug: string }): Observable<Account[]> {
    return this.http
      .get<PaginatedResult<Account>>(
        `${environment.snoots.api.organization}/public/accounts`,
        {
          params: {
            slug
          }
        }
      )
      .pipe(map((response) => response.docs));
  }

  searchPublic(
    request: PageRequest,
    domain?: string
  ): Observable<PaginatedResult<Account>> {
    return this.http.get<PaginatedResult<Account>>(
      `${environment.snoots.api.organization}/public/accounts`,
      {
        params: omitFalsyProperties({
          p: request.page,
          s: request.pageSize,
          domain: domain,
          ...request.filterValues
        })
      }
    );
  }

  @UserNotify({
    successMessage: marker("Account aggiornato"),
    errorMessage: marker("Impossibile aggiornare il account")
  })
  update(id: string, payload: Partial<Account>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.organization}/accounts/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Account rimosso"),
    errorMessage: marker("Impossibile rimuovere il account")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.organization}/accounts/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Account abilitato"),
    errorMessage: marker("Impossibile abilitare il dominio")
  })
  enable(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.organization}/accounts/${id}/enable`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Account disabilitato"),
    errorMessage: marker("Impossibile disabilitare il dominio")
  })
  disable(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.organization}/accounts/${id}/disable`,
      {}
    );
  }
}
